import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { ScrollControls } from './components/ScrollControls';
import { FloatingActionButton } from './components/FloatingActionButton';
import { LoadingScreen } from './components/LoadingScreen';
import { EnhancedParticleBackground } from './components/EnhancedParticleBackground';
import { PageTransition } from './components/PageTransition';
import { ScrollToTop } from './components/ScrollToTop';
import { Toaster } from './components/ui/sonner';
import { HomePage } from './components/pages/HomePage';
import { ProductsPage } from './components/pages/ProductsPage';
import { SolutionsPage } from './components/pages/SolutionsPage';
import { PredictiveMaintenancePage } from './components/pages/PredictiveMaintenancePage';
import { ComputerVisionPage } from './components/pages/ComputerVisionPage';
import { BioinformaticsPage } from './components/pages/BioinformaticsPage';
import { AboutPage } from './components/pages/AboutPage';
import { CaseStudiesPage } from './components/pages/CaseStudiesPage';
import { CaseStudyDetailPage } from './components/pages/CaseStudyDetailPage';
import { CareersPage } from './components/pages/CareersPage';
import { JobDetailPage } from './components/pages/JobDetailPage';
import { ContactPage } from './components/pages/ContactPage';
import { MVSxAI } from './components/pages/products/MVSxAI';
import { ManageRTC } from './components/pages/products/ManageRTC';
import { Websites } from './components/pages/products/Websites';
import { GeneSeqer } from './components/pages/products/GeneSeqer';
import { AIProductsPage } from './components/pages/AIProductsPage';
import { ITProductsPage } from './components/pages/ITProductsPage';
import { IoTProductsPage } from './components/pages/IoTProductsPage';
import { LearningManagementSystem } from './components/pages/products/LearningManagementSystem';
import { WaterLevelIoT } from './components/pages/products/WaterLevelIoT';
import Panaceon from './components/pages/products/Panaceon';
import GreenH2 from './components/pages/products/GreenH2';
import RealEstate from './components/pages/products/RealEstate';
import StyleSync from './components/pages/products/StyleSync';
import TawjihAI from './components/pages/products/TawjihAI';
import WhispeRAGon from './components/pages/products/WhispeRAGon';
import AICooler from './components/pages/products/AICooler';
import CryptoTradingPlatform from './components/pages/products/CryptoTradingPlatform';
import FinancePolicyCheck from './components/pages/products/FinancePolicyCheck';
import Planfusion from './components/pages/products/Planfusion';
import PredictiveMaintenance from './components/pages/products/PredictiveMaintenance';
import AssetManagement from './components/pages/products/AssetManagement';
import { IndustriesPage } from './components/pages/IndustriesPage';
import { BankingFinancialServicesPage } from './components/pages/industries/BankingFinancialServicesPage';
import { ConstructionPage } from './components/pages/industries/ConstructionPage';
import { EducationPage } from './components/pages/industries/EducationPage';
import { HealthcarePage } from './components/pages/industries/HealthcarePage';
import { ManufacturingPage } from './components/pages/industries/ManufacturingPage';
import { OilGasPage } from './components/pages/industries/OilGasPage';
import { RetailEcommercePage } from './components/pages/industries/RetailEcommercePage';
import { TechnologyITServicesPage } from './components/pages/industries/TechnologyITServicesPage';
import { GreenHydrogenPage } from './components/pages/industries/GreenHydrogenPage';
import { MedicalCodingPage as MedicalCodingIndustryPage } from './components/pages/industries/MedicalCodingPage';
import RealEstatePage from './components/pages/industries/RealEstatePage';
import { OperationalIntelligenceAutomationPage } from './components/pages/OperationalIntelligenceAutomationPage';
import { AIPoweredDecisionMakingPage } from './components/pages/AIPoweredDecisionMakingPage';
import { WorkplaceSafetyCompliancePage } from './components/pages/WorkplaceSafetyCompliancePage';
import { DigitalTransformationITEnablementPage } from './components/pages/DigitalTransformationITEnablementPage';
import { BlogListPage } from './components/pages/BlogListPage';
import { BlogPage } from './components/pages/BlogPage';
import { AdminLoginPage } from './components/pages/admin/AdminLoginPage';
import { AdminDashboard } from './components/pages/admin/AdminDashboard';
import { AdminEnquiries } from './components/pages/admin/AdminEnquiries';
import { AdminApplications } from './components/pages/admin/AdminApplications';
import { AdminResumes } from './components/pages/admin/AdminResumes';
import { AdminCareers } from './components/pages/admin/AdminCareers';
import { AdminSettings } from './components/pages/admin/AdminSettings';
import { AdminUsers } from './components/pages/admin/AdminUsers';
import { JobsProvider } from './components/contexts/JobsContext';
import { AdminJobsProvider } from './components/contexts/AdminJobsProvider';
import { SocketProvider } from './components/contexts/SocketContext';
import { ProtectedRoute, SuperAdminRoute } from './components/pages/admin/components';

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/products" element={<PageTransition><ProductsPage /></PageTransition>} />
        
        {/* AI Products */}
        <Route path="/products/ai" element={<PageTransition><AIProductsPage /></PageTransition>} />
        <Route path="/products/ai/ppe-compliance-monitoring" element={<PageTransition><MVSxAI /></PageTransition>} />
        <Route path="/products/ai/mvsxAi" element={<PageTransition><MVSxAI /></PageTransition>} />
        <Route path="/products/ai/geneseqer" element={<PageTransition><GeneSeqer /></PageTransition>} />
        <Route path="/products/ai/LearningManagementSystem" element={<PageTransition><LearningManagementSystem /></PageTransition>} />
        <Route path="/products/ai/Panaceon" element={<PageTransition><Panaceon /></PageTransition>} />
        <Route path="/products/greenh2" element={<PageTransition><GreenH2 /></PageTransition>} />
        <Route path="/products/ai/greenh2" element={<PageTransition><GreenH2 /></PageTransition>} />
        <Route path="/products/real-estate" element={<PageTransition><RealEstate /></PageTransition>} />
        <Route path="/products/ai/real-estate" element={<PageTransition><RealEstate /></PageTransition>} />
        <Route path="/products/style-sync" element={<PageTransition><StyleSync /></PageTransition>} />
        <Route path="/products/ai/style-sync" element={<PageTransition><StyleSync /></PageTransition>} />
        <Route path="/products/ai/tawjihai" element={<PageTransition><TawjihAI /></PageTransition>} />
        <Route path="/products/ai/whisperagon" element={<PageTransition><WhispeRAGon /></PageTransition>} />
        <Route path="/products/ai/aicooler" element={<PageTransition><AICooler /></PageTransition>} />
        <Route path="/products/ai/cryptotradingplatform" element={<PageTransition><CryptoTradingPlatform /></PageTransition>} />
        <Route path="/products/ai/financepolicycheck" element={<PageTransition><FinancePolicyCheck /></PageTransition>} />
        <Route path="/products/ai/planfusion" element={<PageTransition><Planfusion /></PageTransition>} />
        <Route path="/products/ai/predictivemaintenance" element={<PageTransition><PredictiveMaintenance /></PageTransition>} />
        <Route path="/products/ai/assetmanagement" element={<PageTransition><AssetManagement /></PageTransition>} />
        
        {/* IT Products */}
        <Route path="/products/it" element={<PageTransition><ITProductsPage /></PageTransition>} />
        <Route path="/products/it/ManageRTC" element={<PageTransition><ManageRTC /></PageTransition>} />
        <Route path="/products/it/Websites" element={<PageTransition><Websites /></PageTransition>} />
        
        {/* IoT Products */}
        <Route path="/products/iot" element={<PageTransition><IoTProductsPage /></PageTransition>} />
        <Route path="/products/iot/water-level-iot" element={<PageTransition><WaterLevelIoT /></PageTransition>} />
        <Route path="/products/iot/medical-coding" element={<PageTransition><Panaceon /></PageTransition>} />
        
        <Route path="/solutions" element={<PageTransition><SolutionsPage /></PageTransition>} />
        <Route path="/solutions/predictive-maintenance" element={<PageTransition><PredictiveMaintenancePage /></PageTransition>} />
        <Route path="/solutions/computer-vision" element={<PageTransition><ComputerVisionPage /></PageTransition>} />
        <Route path="/solutions/bioinformatics" element={<PageTransition><BioinformaticsPage /></PageTransition>} />
        <Route path="/solutions/operational-intelligence-automation" element={<PageTransition><OperationalIntelligenceAutomationPage /></PageTransition>} />
        <Route path="/solutions/ai-powered-decision-making" element={<PageTransition><AIPoweredDecisionMakingPage /></PageTransition>} />
        <Route path="/solutions/workplace-safety-compliance" element={<PageTransition><WorkplaceSafetyCompliancePage /></PageTransition>} />
        <Route path="/solutions/digital-transformation-it-enablement" element={<PageTransition><DigitalTransformationITEnablementPage /></PageTransition>} />
        
        {/* Industries */}
        <Route path="/industries" element={<PageTransition><IndustriesPage /></PageTransition>} />
        <Route path="/industries/banking-financial-services" element={<PageTransition><BankingFinancialServicesPage /></PageTransition>} />
        <Route path="/industries/construction" element={<PageTransition><ConstructionPage /></PageTransition>} />
        <Route path="/industries/education" element={<PageTransition><EducationPage /></PageTransition>} />
        <Route path="/industries/healthcare" element={<PageTransition><HealthcarePage /></PageTransition>} />
        <Route path="/industries/manufacturing" element={<PageTransition><ManufacturingPage /></PageTransition>} />
        <Route path="/industries/oil-gas" element={<PageTransition><OilGasPage /></PageTransition>} />
        <Route path="/industries/retail-ecommerce" element={<PageTransition><RetailEcommercePage /></PageTransition>} />
        <Route path="/industries/technology-it-services" element={<PageTransition><TechnologyITServicesPage /></PageTransition>} />
        <Route path="/industries/green-hydrogen" element={<PageTransition><GreenHydrogenPage /></PageTransition>} />
        <Route path="/industries/medical-coding" element={<PageTransition><MedicalCodingIndustryPage /></PageTransition>} />
        <Route path="/industries/real-estate" element={<PageTransition><RealEstatePage /></PageTransition>} />
        
        <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
        <Route path="/case-studies" element={<PageTransition><CaseStudiesPage /></PageTransition>} />
        <Route path="/case-studies/:id" element={<PageTransition><CaseStudyDetailPage /></PageTransition>} />
        <Route path="/careers" element={<PageTransition><CareersPage /></PageTransition>} />
        <Route path="/careers/:id" element={<PageTransition><JobDetailPage /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
        <Route path="/blog" element={<PageTransition><BlogListPage /></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><BlogPage /></PageTransition>} />
        
        {/* Admin Routes */}
        <Route path="/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<ProtectedRoute><SocketProvider><AdminJobsProvider><AdminDashboard /></AdminJobsProvider></SocketProvider></ProtectedRoute>} />
        <Route path="/admin/enquiries" element={<ProtectedRoute><SocketProvider><AdminEnquiries /></SocketProvider></ProtectedRoute>} />
        <Route path="/admin/applications" element={<ProtectedRoute><SocketProvider><AdminApplications /></SocketProvider></ProtectedRoute>} />
        <Route path="/admin/resumes" element={<ProtectedRoute><SocketProvider><AdminResumes /></SocketProvider></ProtectedRoute>} />
        <Route path="/admin/careers" element={<ProtectedRoute><SocketProvider><AdminJobsProvider><AdminCareers /></AdminJobsProvider></SocketProvider></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><SocketProvider><AdminSettings /></SocketProvider></ProtectedRoute>} />
        <Route path="/admin/users" element={<SuperAdminRoute><SocketProvider><AdminUsers /></SocketProvider></SuperAdminRoute>} />
        
        {/* Handle preview_page.html and other unmatched routes */}
        <Route path="/preview_page.html" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/login';

  return (
    <div 
      className={`min-h-screen w-full ${!isAdminRoute ? 'bg-background text-foreground' : ''} relative overflow-x-hidden`}
      style={!isAdminRoute ? {
        backgroundColor: '#0A0A0A',
        color: '#FFFFFF',
        isolation: 'isolate'
      } : {}}
    >
      {!isAdminRoute && <EnhancedParticleBackground />}
      {!isAdminRoute && <Navigation />}
      {!isAdminRoute && <ScrollControls />}
      {!isAdminRoute && <FloatingActionButton />}
      
      <main className={`relative z-10 w-full ${!isAdminRoute ? '' : ''}`}>
        <AnimatedRoutes />
      </main>
      
      {!isAdminRoute && <Footer />}
      
      <Toaster 
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          style: {
            background: 'var(--popover)',
            color: 'var(--popover-foreground)',
            border: '1px solid var(--border)',
          },
        }}
      />
    </div>
  );
};

export default function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Preload critical resources
    const preloadResources = () => {
      const imageUrls = [
        'https://images.unsplash.com/photo-1518709268805-4e9042af2176',
        'https://images.unsplash.com/photo-1555949963-ff9fe719c7a1',
        'https://images.unsplash.com/photo-1559757148-5c350d0d3c56',
      ];

      // Preload images only - remove Google Maps preloading
      const imagePromises = imageUrls.map(url => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(url);
          img.onerror = () => resolve(url);
          img.src = url;
        });
      });

      return Promise.all([
        ...imagePromises,
        new Promise(resolve => setTimeout(resolve, 1200)) // Faster loading
      ]);
    };

    preloadResources().then(() => {
      setIsLoading(false);
    });
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  if (isLoading) {
    return <LoadingScreen onComplete={handleLoadingComplete} />;
  }

  return (
    <Router>
      <JobsProvider>
        <ScrollToTop />
        <AppContent />
      </JobsProvider>
    </Router>
  );
}